import type { GlobalWidgetTextSettings, Project, Site, WidgetSettings } from './types.js';

const form = document.getElementById('settings-form') as HTMLFormElement;
const siteSelect = document.getElementById('site-select') as HTMLSelectElement;
const projectSelect = document.getElementById('settings-project-select') as HTMLSelectElement;
const preview = document.getElementById('widget-preview') as HTMLIFrameElement;
const embedCode = document.getElementById('embed-code') as HTMLElement;
const radiusOutput = document.getElementById('radius-output') as HTMLOutputElement;
const messageFontSizeOutput = document.getElementById('message-font-size-output') as HTMLOutputElement;
const globalTextForm = document.getElementById('global-text-settings-form') as HTMLFormElement;

export function fillSiteSelect(sites: Site[], selectedId?: string): Site | null {
  siteSelect.replaceChildren();
  for (const site of sites) {
    const option = document.createElement('option');
    option.value = site.id;
    option.textContent = site.projectName ? `${site.projectName} · ${site.name}` : site.name;
    siteSelect.append(option);
  }
  if (selectedId && sites.some((site) => site.id === selectedId)) siteSelect.value = selectedId;
  const site = sites.find((item) => item.id === siteSelect.value) ?? sites[0] ?? null;
  if (site) fillSettingsForm(site);
  return site;
}

export function fillSettingsProjectSelect(projects: Project[]): void {
  const selected = projectSelect.value;
  projectSelect.replaceChildren();
  const legacy = document.createElement('option');
  legacy.value = '';
  legacy.textContent = 'Без проекта · старый embed-ключ';
  projectSelect.append(legacy);
  for (const project of projects) {
    const option = document.createElement('option');
    option.value = project.id;
    option.textContent = project.name;
    projectSelect.append(option);
  }
  if (selected && projects.some((project) => project.id === selected)) projectSelect.value = selected;
}

export function fillGlobalTextSettings(settings: GlobalWidgetTextSettings): void {
  const title = globalTextForm.elements.namedItem('title') as HTMLInputElement;
  const companyName = globalTextForm.elements.namedItem('companyName') as HTMLInputElement;
  const greeting = globalTextForm.elements.namedItem('greeting') as HTMLTextAreaElement;
  title.value = settings.title;
  companyName.value = settings.companyName;
  greeting.value = settings.greeting;
}

export function readGlobalTextSettings(): GlobalWidgetTextSettings {
  return {
    title: (globalTextForm.elements.namedItem('title') as HTMLInputElement).value,
    companyName: (globalTextForm.elements.namedItem('companyName') as HTMLInputElement).value,
    greeting: (globalTextForm.elements.namedItem('greeting') as HTMLTextAreaElement).value
  };
}

export function fillSettingsForm(site: Site): void {
  siteSelect.value = site.id;
  projectSelect.value = site.projectId ?? '';
  setValue('name', site.name);
  setValue('websiteUrl', site.websiteUrl ?? '');
  setChecked('enabled', site.enabled);
  syncSiteTextControls(site.useGlobalWidgetTexts);
  setValue('title', site.settings.title);
  setValue('companyName', site.settings.companyName);
  setValue('primaryColor', site.settings.primaryColor);
  setValue('position', site.settings.position);
  setValue('greeting', site.settings.greeting);
  setValue('offlineText', site.settings.offlineText);
  setValue('borderRadius', String(site.settings.borderRadius));
  setValue('fontFamily', site.settings.fontFamily);
  setValue('messageFontSize', String(site.settings.messageFontSize));
  setChecked('showAvatar', site.settings.showAvatar);
  setChecked('contactFormEnabled', site.settings.contactForm.enabled);
  setValue('contactFormTrigger', site.settings.contactForm.trigger);
  setValue('contactFormAfterMessages', String(site.settings.contactForm.afterMessages));
  setValue('contactFormTitle', site.settings.contactForm.title);
  setValue('contactFormDescription', site.settings.contactForm.description);
  for (const field of ['name', 'email', 'phone'] as const) {
    setChecked(`contact${capitalize(field)}Enabled`, site.settings.contactForm.fields[field].enabled);
    setChecked(`contact${capitalize(field)}Required`, site.settings.contactForm.fields[field].required);
  }
  syncContactFormControls();
  radiusOutput.value = `${site.settings.borderRadius} px`;
  messageFontSizeOutput.value = `${site.settings.messageFontSize} px`;
  preview.src = `/widget?site=${encodeURIComponent(site.publicKey)}&preview=1`;
  embedCode.textContent = site.projectPublicKey
    ? `<script src="${location.origin}/widget.js" data-project-id="${site.projectPublicKey}"><\/script>`
    : `<script src="${location.origin}/widget.js" data-site-id="${site.publicKey}"><\/script>`;
}

export function readSettingsForm(): {
  projectId: string;
  name: string;
  websiteUrl?: string;
  enabled: boolean;
  settings: WidgetSettings;
} {
  const data = new FormData(form);
  const contactFormEnabled = checked('contactFormEnabled');
  const fields = {
    name: { enabled: checked('contactNameEnabled'), required: checked('contactNameRequired') },
    email: { enabled: checked('contactEmailEnabled'), required: checked('contactEmailRequired') },
    phone: { enabled: checked('contactPhoneEnabled'), required: checked('contactPhoneRequired') }
  };
  if (contactFormEnabled && !Object.values(fields).some((field) => field.enabled)) {
    throw new Error('Включите хотя бы одно поле формы контактов');
  }
  return {
    projectId: String(data.get('projectId') ?? ''),
    name: String(data.get('name') ?? ''),
    ...(String(data.get('websiteUrl') ?? '').trim()
      ? { websiteUrl: String(data.get('websiteUrl')).trim() }
      : {}),
    enabled: (form.elements.namedItem('enabled') as HTMLInputElement).checked,
    settings: {
      title: String(data.get('title') ?? ''),
      companyName: String(data.get('companyName') ?? ''),
      primaryColor: String(data.get('primaryColor') ?? '#e35b45'),
      position: String(data.get('position')) === 'left' ? 'left' : 'right',
      greeting: String(data.get('greeting') ?? ''),
      offlineText: String(data.get('offlineText') ?? ''),
      borderRadius: Number(data.get('borderRadius') ?? 18),
      fontFamily: ['Manrope', 'Inter'].includes(String(data.get('fontFamily')))
        ? String(data.get('fontFamily')) as 'Manrope' | 'Inter'
        : 'System',
      messageFontSize: Number(data.get('messageFontSize') ?? 15),
      showAvatar: checked('showAvatar'),
      contactForm: {
        enabled: contactFormEnabled,
        trigger: String(data.get('contactFormTrigger')) === 'after-messages' ? 'after-messages' : 'before-chat',
        afterMessages: Number((form.elements.namedItem('contactFormAfterMessages') as HTMLInputElement).value || 1),
        title: (form.elements.namedItem('contactFormTitle') as HTMLInputElement).value,
        description: (form.elements.namedItem('contactFormDescription') as HTMLTextAreaElement).value,
        fields
      }
    }
  };
}

export function currentSiteId(): string {
  return siteSelect.value;
}

export function currentEmbedCode(): string {
  return embedCode.textContent ?? '';
}

export function bindSettingsPreview(onSiteChange: (id: string) => void): void {
  siteSelect.addEventListener('change', () => onSiteChange(siteSelect.value));
  (form.elements.namedItem('borderRadius') as HTMLInputElement).addEventListener('input', (event) => {
    radiusOutput.value = `${(event.target as HTMLInputElement).value} px`;
  });
  (form.elements.namedItem('messageFontSize') as HTMLInputElement).addEventListener('input', (event) => {
    messageFontSizeOutput.value = `${(event.target as HTMLInputElement).value} px`;
  });
  (form.elements.namedItem('contactFormEnabled') as HTMLInputElement).addEventListener('change', syncContactFormControls);
  (form.elements.namedItem('contactFormTrigger') as HTMLSelectElement).addEventListener('change', syncContactFormControls);
  for (const field of ['Name', 'Email', 'Phone']) {
    (form.elements.namedItem(`contact${field}Enabled`) as HTMLInputElement).addEventListener('change', syncContactFormControls);
  }
}

export function refreshWidgetPreview(): void {
  if (preview.src) preview.src = preview.src;
}

function syncSiteTextControls(useGlobalWidgetTexts: boolean): void {
  const siteTexts = document.getElementById('site-text-settings') as HTMLElement;
  siteTexts.classList.toggle('is-globally-overridden', useGlobalWidgetTexts);
  document.getElementById('site-text-settings-notice')?.classList.toggle('is-hidden', !useGlobalWidgetTexts);
  for (const name of ['title', 'companyName', 'greeting']) {
    const input = form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement;
    input.readOnly = useGlobalWidgetTexts;
  }
}

function syncContactFormControls(): void {
  const enabled = checked('contactFormEnabled');
  const body = document.getElementById('contact-form-settings-body') as HTMLFieldSetElement;
  body.disabled = !enabled;
  body.classList.toggle('is-disabled', !enabled);
  const afterMessages = form.elements.namedItem('contactFormAfterMessages') as HTMLInputElement;
  const trigger = form.elements.namedItem('contactFormTrigger') as HTMLSelectElement;
  afterMessages.closest('label')?.classList.toggle('is-hidden', trigger.value !== 'after-messages');
  for (const field of ['Name', 'Email', 'Phone']) {
    const shown = form.elements.namedItem(`contact${field}Enabled`) as HTMLInputElement;
    const required = form.elements.namedItem(`contact${field}Required`) as HTMLInputElement;
    if (!shown.checked) required.checked = false;
    required.disabled = !enabled || !shown.checked;
  }
}

function setValue(name: string, value: string): void {
  (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value = value;
}

function setChecked(name: string, value: boolean): void {
  (form.elements.namedItem(name) as HTMLInputElement).checked = value;
}

function checked(name: string): boolean {
  return (form.elements.namedItem(name) as HTMLInputElement).checked;
}

function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
